import * as React from 'react'
import cn from 'clsx'
import { z } from 'zod'
import { Button, Callout, Card, Dialog, Heading, Text } from '@radix-ui/themes'
import { useTranslation } from 'react-i18next'
import { useFetcher, useLocation, useOutletContext, useSearchParams } from '@remix-run/react'
import { BiSolidOffer } from 'react-icons/bi'
import { RiCheckboxCircleLine } from 'react-icons/ri'
import { useHydrated } from 'remix-utils/use-hydrated'

import { INDEX_I18NEXT } from '~/routes/($lang)._index/index.component'
import { appView, Context, FormEnum, ROOT_INPUTS } from '~/routes/_layout/root.component'
import { camelToSnake } from '~/utils/camel-to-snake'
import { schemaDefault } from '~/utils/form-defaults.ts'
import { RadioGroup } from '~/components/ui/radio-group.tsx'
import { TextField } from '~/components/ui/text-field.tsx'
import {
  LOGIN_FORM,
  LOGIN_INPUTS,
  LOGIN_SEARCH_PARAMS,
  loginSchema,
} from '~/routes/($lang)._auth.login/login.component.tsx'
import { FormProvider, getFormProps, useForm } from '@conform-to/react'
import { parseWithZod } from '@conform-to/zod'
import { FormDefaults } from '~/components/common/form-defaults.tsx'

/* -------------------------------------------------------------------------------------------------
 * Search params
 * -----------------------------------------------------------------------------------------------*/

enum SEARCH_PARAMS {
  redirect = 'redirect',
  offerRequested = 'offer-requested',
}

/* -------------------------------------------------------------------------------------------------
 * Inputs
 * -----------------------------------------------------------------------------------------------*/

enum INPUTS {
  subaction = 'subaction',
  redirect = 'redirect',
  roofType = 'roofType',
  youWant = 'youWant',
  squareFootOfRoof = 'squareFootOfRoof',
  needWallbox = 'needWallbox',
  needEnergyStorageSystem = 'needEnergyStorageSystem',
  name = 'name',
  email = 'email',
  phone = 'phone',
  postcode = 'postcode',
  street = 'street',
}

/* -------------------------------------------------------------------------------------------------
 * Form
 * -----------------------------------------------------------------------------------------------*/

const FORM: FormEnum = {
  id: 'offer-form',
  action: '/?index',
  fetcherKey: 'offer',
  subaction: 'offer',
  redirect: '/',
} as const

const schema = z.object({
  ...schemaDefault,
  ...{
    [INPUTS.roofType]: z
      .array(
        z
          .string()
      )
      .nonempty('validation.roofType.nonEmpty'),
    [INPUTS.youWant]: z
      .array(
        z
          .string()
      )
      .nonempty('validation.youWant.nonEmpty'),
    [INPUTS.squareFootOfRoof]: z
      .array(
        z
          .string()
      )
      .nonempty('validation.squareFootOfRoof.nonEmpty'),
    [INPUTS.needWallbox]: z
      .array(
        z
          .string()
      )
      .nonempty('validation.needWallbox.nonEmpty'),
    [INPUTS.needEnergyStorageSystem]: z
      .array(
        z
          .string()
      )
      .nonempty('validation.needEnergyStorageSystem.nonEmpty'),
    [INPUTS.name]: z
      .string({ required_error: 'validation.name.required' })
      .min(2, 'validation.name.min')
      .max(255, 'validation.name.max'),
    [INPUTS.email]: z
      .string({ required_error: 'validation.email.required' })
      .email('validation.email.email')
      .min(2, 'validation.email.min')
      .max(255, 'validation.email.max'),
    [INPUTS.phone]: z
      .string({ required_error: 'validation.phone.required' })
      .min(2, 'validation.phone.min')
      .max(255, 'validation.phone.max'),
    [INPUTS.postcode]: z
      .string({ required_error: 'validation.postcode.required' })
      .length(5, 'validation.postcode.length'),
    [INPUTS.street]: z
      .string()
      .min(2, 'validation.street.min')
      .max(255, 'validation.street.max')
      .optional(),
  }
})

const Offer: React.FC = () => {
  const { setApp } = useOutletContext<Context>()
  const [searchParams] = useSearchParams()
  const { t } = useTranslation(INDEX_I18NEXT.routeIndex)


  return (
    <div className="flex justify-center">
      <Card size="3" className="app-Offer relative">
        <div className="absolute -top-3 -right-3 z-30">
          <BiSolidOffer size="2.4em" className="text-accent-10" />
        </div>
        <div className="flex flex-col items-center gap-y-4">
          {searchParams.get(SEARCH_PARAMS.offerRequested) === 'on' && (
            <Callout.Root color="green">
              <Callout.Icon>
                <RiCheckboxCircleLine size="1.2em" />
              </Callout.Icon>
              <Callout.Text>
                {t('common.offer.callout.text')}
              </Callout.Text>
            </Callout.Root>
          )}
          <Heading color="green" weight="medium" size="7" className="text-center">
            {t('common.offer.title')}
          </Heading>
          <Text color="gray" className="text-center">
            {t('common.offer.description')}
          </Text>
          <div className="flex flex-col items-center justify-center gap-y-6">
            <Button
              variant="soft"
              size="3"
              onClick={() => setApp((prev) => ({
                ...prev,
                modalView: appView.offerView,
                displayModal: true,
              }))}
            >
              {t('common.offer.dialog.buttonTrigger')}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  )
}

export {
  Offer,
  SEARCH_PARAMS as OFFER_SEARCH_PARAMS,
  INPUTS as OFFER_INPUTS,
  FORM as OFFER_FORM,
  schema as offerSchema,
}
